import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelectWithAdd } from '../components/ui/multi-select-with-add';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { DEPARTMENTS } from '../lib/constants';
import { useToastStore } from '../store/toastStore';
import { GuidelinesSidebar } from '../components/GuidelinesSidebar';

const SUPPORT_TYPES = [
  'Data governance check', 'Access rights & permissions',
  'Idea validation', 'Feasibility check', 'Tool recommendations',
  'Prompt engineering', 'Hands-on pairing', 'MCP/API integration',
  'Demo prep', 'Troubleshooting', 'Other',
];

const FRONTEND_OPTIONS = ['React', 'Next.js', 'Svelte', 'Vue', 'Angular'];
const BACKEND_OPTIONS = ['Node.js', 'Express', 'FastAPI', 'Spring Boot', 'Go', 'Django', 'NestJS'];
const AI_TOOLS_OPTIONS = ['Kiro', 'Cursor', 'GitHub Copilot', 'Claude Code', 'ChatGPT', 'n8n', 'LangChain', 'Vercel AI SDK'];
const INTEGRATIONS_OPTIONS = ['Jira', 'Slack', 'Bitbucket', 'GitHub', 'Confluence', 'Google Calendar', 'Supabase', 'AWS'];
const DATABASE_OPTIONS = ['PostgreSQL', 'Supabase', 'AWS RDS', 'MongoDB', 'MySQL', 'Redis'];

interface FormData {
  title: string;
  department: string;
  building_type: string;
  problem_statement: string;
  proposed_solution: string;
  expected_impact: string;
  ai_maturity_level: string;
  current_status: string;
  urgency: string;
  urgency_reason: string;
  support_needs: string[];
  support_other_text: string;
  where_stuck: string;
  tech_frontend: string[];
  tech_backend: string[];
  tech_database: string[];
  tech_ai_tools: string[];
  tech_integrations: string[];
  other_details: string;
  doc_link: string;
  hld_link: string;
  git_repo_url: string;
  staging_url: string;
  production_url: string;
}

export function SubmitPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditing = Boolean(editId);
  const { user } = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const showToast = useToastStore((s) => s.show);

  const [form, setForm] = useState<FormData>({
    title: '', department: '', building_type: '', problem_statement: '', proposed_solution: '',
    expected_impact: '', ai_maturity_level: '', current_status: '', urgency: '', urgency_reason: '',
    support_needs: [], support_other_text: '', where_stuck: '',
    tech_frontend: [], tech_backend: [], tech_database: [], tech_ai_tools: [], tech_integrations: [],
    other_details: '', doc_link: '',
    hld_link: '', git_repo_url: '', staging_url: '', production_url: '',
  });

  const updateField = useCallback((field: keyof FormData, value: string | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  useEffect(() => {
    if (!editId) return;

    const loadProposal = async () => {
      try {
        const res = await api.get<{
          proposal: FormData & { status: string };
          supportNeeds: { support_type: string; other_text?: string }[];
        }>(`/proposals/${editId}`);

        if (!['draft', 'needs_clarification'].includes(res.proposal.status)) {
          showToast('This proposal cannot be edited', 'error');
          navigate(`/proposals/${editId}`);
          return;
        }

        const supportNeeds = res.supportNeeds?.map((s) => s.support_type) || [];
        const otherSupport = res.supportNeeds?.find((s) => s.support_type === 'Other');

        setForm({
          title: res.proposal.title || '',
          department: res.proposal.department || '',
          building_type: res.proposal.building_type || '',
          problem_statement: res.proposal.problem_statement || '',
          proposed_solution: res.proposal.proposed_solution || '',
          expected_impact: res.proposal.expected_impact || '',
          ai_maturity_level: res.proposal.ai_maturity_level || '',
          current_status: res.proposal.current_status || '',
          urgency: res.proposal.urgency || '',
          urgency_reason: res.proposal.urgency_reason || '',
          support_needs: supportNeeds,
          support_other_text: otherSupport?.other_text || '',
          where_stuck: res.proposal.where_stuck || '',
          tech_frontend: res.proposal.tech_frontend || [],
          tech_backend: res.proposal.tech_backend || [],
          tech_database: res.proposal.tech_database || [],
          tech_ai_tools: res.proposal.tech_ai_tools || [],
          tech_integrations: res.proposal.tech_integrations || [],
          other_details: res.proposal.other_details || '',
          doc_link: res.proposal.doc_link || '',
          hld_link: res.proposal.hld_link || '',
          git_repo_url: res.proposal.git_repo_url || '',
          staging_url: res.proposal.staging_url || '',
          production_url: res.proposal.production_url || '',
        });
      } catch (err: any) {
        showToast(err.message || 'Failed to load proposal', 'error');
        navigate('/my-proposals');
      }
      setLoading(false);
    };

    loadProposal();
  }, [editId, navigate, showToast]);

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setSubmitError('');
    try {
      const payload = { ...form, submit: false };
      if (isEditing && editId) {
        await api.patch(`/proposals/${editId}`, payload);
      } else {
        const res = await api.post<{ proposal: { id: string } }>('/proposals', payload);
        navigate(`/submit?id=${res.proposal.id}`, { replace: true });
      }
      showToast('Draft saved', 'success');
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to save draft');
    }
    setSavingDraft(false);
  };

  const handleSubmit = async () => {
    // Check required fields are not empty
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = 'Required';
    if (!form.department) errs.department = 'Required';
    if (!form.building_type) errs.building_type = 'Required';
    if (!form.problem_statement.trim()) errs.problem_statement = 'Required';
    if (!form.proposed_solution.trim()) errs.proposed_solution = 'Required';
    if (!form.expected_impact.trim()) errs.expected_impact = 'Required';
    if (!form.ai_maturity_level) errs.ai_maturity_level = 'Required';
    if (!form.current_status) errs.current_status = 'Required';
    if (!form.urgency) errs.urgency = 'Required';
    if (form.support_needs.length === 0) errs.support_needs = 'Required';

    const showArtifacts = ['partial', 'nearly_complete', 'stuck'].includes(form.current_status);
    if (showArtifacts && !form.git_repo_url.trim()) {
      errs.git_repo_url = 'Required for partially built projects';
    }
    const urlFields = ['doc_link', 'hld_link', 'git_repo_url', 'staging_url', 'production_url'] as const;
    for (const field of urlFields) {
      const val = form[field].trim();
      if (val && !/^https?:\/\/.+/i.test(val)) {
        errs[field] = 'Must be a valid http or https URL';
      }
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = { ...form, submit: true };
      if (isEditing && editId) {
        await api.patch(`/proposals/${editId}`, payload);
      } else {
        await api.post('/proposals', payload);
      }
      showToast('Proposal submitted successfully', 'success');
      navigate('/my-proposals');
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit proposal');
    }
    setSubmitting(false);
  };

  const toggleSupport = (type: string) => {
    setForm((prev) => ({
      ...prev,
      support_needs: prev.support_needs.includes(type)
        ? prev.support_needs.filter((t) => t !== type)
        : [...prev.support_needs, type],
    }));
  };

  const showArtifactFields = ['partial', 'nearly_complete', 'stuck'].includes(form.current_status);

  if (loading) {
    return <p className="text-muted-foreground">Loading proposal...</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">
          {isEditing ? 'Edit Proposal' : 'Submit Proposal'}
        </h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 space-y-6">

      {/* Section 1 */}
      <Card>
        <CardHeader><CardTitle className="text-base">1. Requester Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label>Your Name</Label>
            <Input value={user?.name || ''} disabled />
          </div>
          <div id="field-department">
            <Label>Department *</Label>
            <Select value={form.department} onValueChange={(val) => { updateField('department', val); setErrors((e) => ({ ...e, department: '' })); }}>
              <SelectTrigger className={errors.department ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.department && <p className="text-xs text-destructive mt-1">{errors.department}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Section 2 */}
      <Card>
        <CardHeader><CardTitle className="text-base">2. Project Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div id="field-title">
            <Label>Project Title * <span className="text-muted-foreground text-xs">({form.title.length}/50)</span></Label>
            <Input maxLength={50} value={form.title} onChange={(e) => { updateField('title', e.target.value); setErrors((er) => ({ ...er, title: '' })); }} className={errors.title ? 'border-destructive' : ''} />
            {errors.title && <p className="text-xs text-destructive mt-1">{errors.title}</p>}
          </div>
          <div id="field-building_type">
            <Label>What are you building? *</Label>
            <Select value={form.building_type} onValueChange={(val) => { updateField('building_type', val); setErrors((e) => ({ ...e, building_type: '' })); }}>
              <SelectTrigger className={errors.building_type ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tool_building">Tool Building with AI</SelectItem>
                <SelectItem value="workflow_automation">Workflow & Automation</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.building_type && <p className="text-xs text-destructive mt-1">{errors.building_type}</p>}
          </div>
          <div id="field-problem_statement">
            <Label>What problem or pain point are you solving? * <span className="text-muted-foreground text-xs">({form.problem_statement.length}/500)</span></Label>
            <Textarea maxLength={500} value={form.problem_statement} onChange={(e) => { updateField('problem_statement', e.target.value); setErrors((er) => ({ ...er, problem_statement: '' })); }} rows={3} className={errors.problem_statement ? 'border-destructive' : ''} />
            {errors.problem_statement && <p className="text-xs text-destructive mt-1">{errors.problem_statement}</p>}
          </div>
          <div id="field-proposed_solution">
            <Label>What would the finished solution look like? * <span className="text-muted-foreground text-xs">({form.proposed_solution.length}/500)</span></Label>
            <Textarea maxLength={500} value={form.proposed_solution} onChange={(e) => { updateField('proposed_solution', e.target.value); setErrors((er) => ({ ...er, proposed_solution: '' })); }} rows={3} className={errors.proposed_solution ? 'border-destructive' : ''} placeholder="i.e. what's in scope?" />
            {errors.proposed_solution && <p className="text-xs text-destructive mt-1">{errors.proposed_solution}</p>}
          </div>
          <div id="field-expected_impact">
            <Label>What is the expected outcome? * <span className="text-muted-foreground text-xs">({form.expected_impact.length}/300)</span></Label>
            <Textarea maxLength={300} value={form.expected_impact} onChange={(e) => { updateField('expected_impact', e.target.value); setErrors((er) => ({ ...er, expected_impact: '' })); }} rows={2} className={errors.expected_impact ? 'border-destructive' : ''} placeholder="E.g. reduced turnaround times, time savings, ease of use, etc." />
            {errors.expected_impact && <p className="text-xs text-destructive mt-1">{errors.expected_impact}</p>}
          </div>
          <div id="field-ai_maturity_level">
            <Label>AI Maturity Level *</Label>
            <Select value={form.ai_maturity_level} onValueChange={(val) => { updateField('ai_maturity_level', val); setErrors((e) => ({ ...e, ai_maturity_level: '' })); }}>
              <SelectTrigger className={errors.ai_maturity_level ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select AI maturity level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="L0">L0 — AI Aware</SelectItem>
                <SelectItem value="L1">L1 — AI Prompted</SelectItem>
                <SelectItem value="L2">L2 — AI Builder</SelectItem>
                <SelectItem value="L3">L3 — AI Integrated</SelectItem>
                <SelectItem value="L4">L4 — AI Orchestrated</SelectItem>
                <SelectItem value="L5">L5 — AI Native</SelectItem>
              </SelectContent>
            </Select>
            {errors.ai_maturity_level && <p className="text-xs text-destructive mt-1">{errors.ai_maturity_level}</p>}
          </div>
          <div id="field-current_status">
            <Label>Current Status *</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[
                { value: 'idea', label: 'Idea' },
                { value: 'exploring', label: 'Exploring' },
                { value: 'partial', label: 'Partially Built' },
                { value: 'nearly_complete', label: 'Nearly Complete' },
                { value: 'stuck', label: 'Stuck' },
              ].map((s) => (
                <Button
                  key={s.value}
                  type="button"
                  variant={form.current_status === s.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { updateField('current_status', s.value); setErrors((er) => ({ ...er, current_status: '' })); }}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            {errors.current_status && <p className="text-xs text-destructive mt-1">{errors.current_status}</p>}
          </div>
          <div id="field-urgency">
            <Label>Timeline / Urgency *</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[
                { value: 'two_weeks', label: '2 Weeks' },
                { value: 'one_month', label: '1 Month' },
                { value: 'one_quarter', label: '1 Quarter' },
                { value: 'no_deadline', label: 'No Deadline' },
              ].map((u) => (
                <Button
                  key={u.value}
                  type="button"
                  variant={form.urgency === u.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { updateField('urgency', u.value); setErrors((er) => ({ ...er, urgency: '' })); }}
                >
                  {u.label}
                </Button>
              ))}
            </div>
            {errors.urgency && <p className="text-xs text-destructive mt-1">{errors.urgency}</p>}
          </div>
          {form.urgency === 'two_weeks' && (
            <div>
              <Label>Urgency Reason</Label>
              <Input value={form.urgency_reason} onChange={(e) => updateField('urgency_reason', e.target.value)} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Tech Stack */}
      <Card>
        <CardHeader><CardTitle className="text-base">3. Tech Stack</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <MultiSelectWithAdd
            label="Integrations & Connectors"
            options={INTEGRATIONS_OPTIONS}
            selected={form.tech_integrations}
            onChange={(val) => updateField('tech_integrations', val)}
            placeholder="Add integration..."
          />
          <MultiSelectWithAdd
            label="AI Tools"
            options={AI_TOOLS_OPTIONS}
            selected={form.tech_ai_tools}
            onChange={(val) => updateField('tech_ai_tools', val)}
            placeholder="Add AI tool..."
          />
          <MultiSelectWithAdd
            label="Database"
            options={DATABASE_OPTIONS}
            selected={form.tech_database}
            onChange={(val) => updateField('tech_database', val)}
            placeholder="Add database..."
          />
          <MultiSelectWithAdd
            label="Frontend"
            options={FRONTEND_OPTIONS}
            selected={form.tech_frontend}
            onChange={(val) => updateField('tech_frontend', val)}
            placeholder="Add frontend tech..."
          />
          <MultiSelectWithAdd
            label="Backend"
            options={BACKEND_OPTIONS}
            selected={form.tech_backend}
            onChange={(val) => updateField('tech_backend', val)}
            placeholder="Add backend tech..."
          />
        </CardContent>
      </Card>

      {/* Section 4 */}
      <Card>
        <CardHeader><CardTitle className="text-base">4. Support Needed</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div id="field-support_needs">
            <Label>What support do you need? *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {SUPPORT_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.support_needs.includes(type)}
                    onChange={() => { toggleSupport(type); setErrors((er) => ({ ...er, support_needs: '' })); }}
                    className="rounded border-input"
                  />
                  {type}
                </label>
              ))}
            </div>
            {errors.support_needs && <p className="text-xs text-destructive mt-1">{errors.support_needs}</p>}
          </div>
          {form.support_needs.includes('Other') && (
            <div>
              <Label>Other (specify)</Label>
              <Input value={form.support_other_text} onChange={(e) => updateField('support_other_text', e.target.value)} />
            </div>
          )}
          <div>
            <Label>Where you're stuck (optional) <span className="text-muted-foreground text-xs">({form.where_stuck.length}/300)</span></Label>
            <Textarea maxLength={300} value={form.where_stuck} onChange={(e) => updateField('where_stuck', e.target.value)} rows={2} />
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Any Other Details */}
      <Card>
        <CardHeader><CardTitle className="text-base">5. Any Other Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Textarea
              placeholder="Add any additional context, links, references, or notes that might help the committee review your proposal..."
              value={form.other_details}
              onChange={(e) => updateField('other_details', e.target.value)}
              rows={4}
            />
            <p className="text-xs text-muted-foreground mt-1">Optional</p>
          </div>
          <div>
            <Label>Design doc / HLD</Label>
            <Input
              value={form.doc_link}
              onChange={(e) => updateField('doc_link', e.target.value)}
              placeholder="https://docs.google.com/document/d/..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Google Doc or design document link. Optional but recommended.
            </p>
            {errors.doc_link && <p className="text-xs text-destructive mt-1">{errors.doc_link}</p>}
          </div>
          {showArtifactFields && (
            <div className="space-y-4 pt-2 border-t">
              <p className="text-sm font-medium">Project Artifacts</p>
              <div id="field-hld_link">
                <Label>HLD Link (optional)</Label>
                <Input
                  value={form.hld_link}
                  onChange={(e) => updateField('hld_link', e.target.value)}
                  placeholder="https://..."
                />
                {errors.hld_link && <p className="text-xs text-destructive mt-1">{errors.hld_link}</p>}
              </div>
              <div id="field-git_repo_url">
                <Label>Git Repository URL *</Label>
                <Input
                  value={form.git_repo_url}
                  onChange={(e) => { updateField('git_repo_url', e.target.value); setErrors((er) => ({ ...er, git_repo_url: '' })); }}
                  placeholder="https://github.com/..."
                  className={errors.git_repo_url ? 'border-destructive' : ''}
                />
                {errors.git_repo_url && <p className="text-xs text-destructive mt-1">{errors.git_repo_url}</p>}
              </div>
              <div id="field-staging_url">
                <Label>Staging URL (optional)</Label>
                <Input
                  value={form.staging_url}
                  onChange={(e) => updateField('staging_url', e.target.value)}
                  placeholder="https://staging.example.com"
                />
                {errors.staging_url && <p className="text-xs text-destructive mt-1">{errors.staging_url}</p>}
              </div>
              <div id="field-production_url">
                <Label>Production URL (optional)</Label>
                <Input
                  value={form.production_url}
                  onChange={(e) => updateField('production_url', e.target.value)}
                  placeholder="https://app.example.com"
                />
                {errors.production_url && <p className="text-xs text-destructive mt-1">{errors.production_url}</p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col items-end gap-2">
        {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate('/')}>Cancel</Button>
        <Button variant="outline" onClick={handleSaveDraft} disabled={savingDraft || submitting}>
          {savingDraft ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button onClick={handleSubmit} disabled={submitting || savingDraft}>
          {submitting ? 'Submitting...' : 'Submit Proposal'}
        </Button>
        </div>
      </div>
    </div>

      {/* Guidelines Sidebar */}
      <GuidelinesSidebar />
      </div>
    </div>
  );
}
